import { isFoodBookingModel } from '@xeetr/types/utils'
import { ManagementTenant, FoodBookingModel, CalendarResource } from '@xeetr/types/models'

/**
 * Calculate how many people and tables are in use based on booking
 * **start times** only.
 *
 * The function checks each booking's `from` date and includes it if the
 * booking starts within the half-open interval
 * `[date, date + timeRangeMinutes)`. Bookings that begin before the window or
 * exactly at `date + timeRangeMinutes` are excluded. Bookings without an
 * `until` value are ignored entirely (they're treated as open-ended).
 *
 * `totalTables` is the number of counted bookings (one per booking, not the
 * booking's `tables.length`). Catering bookings are excluded from both totals.
 *
 * @param bookings - list of bookings to inspect
 * @param tenant - tenant context (only used for sanity check, must be
 *                 non-null or the result is `null`)
 * @param date - the moment at which the range begins
 * @param timeRangeMinutes - length of the window in minutes; defaults to 60.
 */
export const calculateTableUtilizationForTime = (
  bookings: FoodBookingModel[],
  tenant: ManagementTenant,
  date: Date,
  timeRangeMinutes = 60
): { totalPeople: number; totalTables: number } | null => {
  if (!tenant || !date) return null

  const targetDate = date
  let totalPeople = 0
  let totalTables = 0

  for (const booking of bookings) {
    const cateringFieldId = tenant.bookingCustomFields?.find(
      (field) => field.name === 'Catering'
    )?.id
    if (
      !isFoodBookingModel(booking) ||
      (cateringFieldId && booking.bookingAdditions?.customFields?.[cateringFieldId])
    )
      continue

    if (!booking.until) continue

    const start = booking.from.getTime()
    const t = targetDate.getTime()

    const startSec = Math.floor(start / 1000)
    const tSec = Math.floor(t / 1000)

    // include only bookings whose start falls inside [tSec, tSec + timeRangeMinutes*60)
    if (startSec >= tSec && startSec < tSec + timeRangeMinutes * 60) {
      totalPeople += booking.amount
      totalTables += 1
    }
  }

  return { totalPeople, totalTables }
}

export interface SlotInfo {
  year: number
  month: number
  day: number
  hour: number
  minute: number
}

export const slotInfoToDate = ({ year, month, day, hour, minute }: SlotInfo): Date => {
  return new Date(year, month, day, hour, minute)
}

/**
 * Order calendar resources (rooms and tables) taking tenant preferences into
 * account. Rooms without a custom order will use their own `order` field. In
 * all cases tables are grouped under their parent room and sorted by their own
 * `order` value. Orphan tables (those whose parent is not present) are appended
 * at the end, also sorted by `order`.
 *
 * @param resources - list of resources from the API
 * @param calendarRoomOrder - optional array of room ids representing a custom
 *                            ordering defined in tenant settings
 * @returns a new array of resources in the desired order
 */
export function orderResources(
  resources: CalendarResource[],
  calendarRoomOrder?: string[]
): CalendarResource[] {
  if (!resources || resources.length === 0) return []

  const rooms = resources.filter((r) => !r.parentId)
  const tables = resources.filter((r) => r.parentId)

  // helper to sort by numeric order property
  const byOrder = (a: CalendarResource, b: CalendarResource) => a.order - b.order

  // if no tenant-provided order, keep the original room order but still sort
  // tables within each room and orphan tables.
  if (!calendarRoomOrder || calendarRoomOrder.length === 0) {
    const result: CalendarResource[] = []
    rooms.forEach((room) => {
      result.push(room)
      const roomTables = tables.filter((t) => t.parentId === room.id).sort(byOrder)
      result.push(...roomTables)
    })
    const orphanTables = tables.filter((t) => !rooms.find((r) => r.id === t.parentId)).sort(byOrder)
    result.push(...orphanTables)
    return result
  }

  // build a map for quick lookup of tenant ordering
  const orderMap = new Map<string, number>(calendarRoomOrder.map((id, index) => [id, index]))

  const sortedRooms = [...rooms].sort((a, b) => {
    const orderA = orderMap.get(a.id)
    const orderB = orderMap.get(b.id)

    if (orderA !== undefined && orderB !== undefined) return orderA - orderB
    if (orderA !== undefined) return -1
    if (orderB !== undefined) return 1
    // fallback to the room's own order property
    return a.order - b.order
  })

  const result: CalendarResource[] = []
  sortedRooms.forEach((room) => {
    result.push(room)
    const roomTables = tables.filter((t) => t.parentId === room.id).sort(byOrder)
    result.push(...roomTables)
  })

  const orphanTables = tables
    .filter((t) => !sortedRooms.find((r) => r.id === t.parentId))
    .sort(byOrder)
  result.push(...orphanTables)

  return result
}
